"use client"

import React, { useRef, Children, isValidElement, useMemo } from "react"
import { useScroll, useMotionValueEvent } from "framer-motion"

interface TextRevealProps {
  text?: string
  children?: React.ReactNode
  splitBy?: "char" | "word"
  className?: string
}

const TextReveal: React.FC<TextRevealProps> = ({ text, children, splitBy = "word", className = "" }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [scrollProgress, setScrollProgress] = React.useState(0)

  // Set up scroll-linked animation at the top level
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "30% center"],
  })

  // Update state when scroll progress changes
  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    setScrollProgress(latest)
  })

  // Split text content into array based on splitBy prop
  const contentArray = useMemo(() => {
    if (text && !children) {
      return splitBy === "word"
        ? text.split(/(\s+)/) // Preserve whitespace
        : text.split("")
    }
    return []
  }, [text, children, splitBy])

  // Calculate how many items should be visible
  const textLength = text ? contentArray.length : Children.count(children || [])
  const visibleCount = Math.ceil(scrollProgress * textLength)

  return (
    <div ref={containerRef} className={`whitespace-pre-wrap text-justify leading-relaxed ${className}`}>
      {text && (
        <div>
          {contentArray.map((item, i) => (
            <span
              key={i}
              className={`transition-opacity duration-300 ${item.trim() === "" ? "" : "inline-block"}`}
              style={{
                opacity: i < visibleCount ? 1 : 0.1,
              }}
            >
              {item}
            </span>
          ))}
        </div>
      )}

      {!text && children && (
        <div>
          {Children.map(children, (child, i) => {
            if (!isValidElement(child)) return null
            return (
              <div
                key={i}
                className="transition-opacity duration-500"
                style={{
                  opacity: i < visibleCount ? 1 : 0.1,
                }}
              >
                {child}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default TextReveal
